import * as React from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  makeStyles,
  MenuItem,
  Select,
  IconButton
} from '@material-ui/core';
import { Clear, Delete } from '@material-ui/icons';
import clsx from 'clsx';
import * as styles from './attribute-row.module.scss';
import * as buildStyles from './build-segment.module.scss';
import {
  GroupByFilter,
  SegmentAttributeDatetime,
  SegmentAttributeFilterRender,
  SegmentAttributeOperator,
  SegmentAttributeOptions,
  SegmentAttributeTransactional,
  SegmentFilterOperator,
  SelectedAttribute,
  SelectedAttributeDaterange,
  SelectedAttributeInput,
  SelectedAttributeOption,
  SelectedAttributeRange,
  SelectedAttributeTransactional,
  TransactionalFilter
} from '../../../models/segments';
import { TertiaryButton, SecondaryButton } from 'kagr-ui';
import { useBuildSegment } from './useBuildSegment';
import AddAttribute from './add-attribute-button';
import AttributeFilterOptions from './attribute-filter-options';
import AttributeFilterDatetime from './attribute-filter-datetime';
import moment from 'moment';
import AttributeFilterRange from './attribute-filter-range';
import { blue, colors } from '../../../theme/colors';
import _ from 'lodash';
import OperatorSelect from './operator-select';
import { MultiselectChip } from './multiselect-chip';
import { evalAttributeFilterRenderType } from '../../../utilities/attribute-filter';
import AttributeFilterInput from './attribute-filter-input';
import AttributeGroupBy from './attribute-group-by';

type TransactionalAttribute = SegmentAttributeTransactional & {
  transactionalFilters: TransactionalFilter[];
};

interface Props {
  attribute: SegmentAttributeTransactional;
  selectedAttribute: SelectedAttributeTransactional;
  position: number[];
  exclude?: boolean;
  attributeIndex: number;
}

const breakpoints = {
  xs: 640,
  sm: 1020,
  md: 1200
};

const useStyles = makeStyles((theme) => ({
  dialog: {
    [theme.breakpoints.up('md')]: {
      '& .MuiDialog-paperWidthSm': {
        width: '83rem',
        minWidth: '83rem'
      }
    }
  },
  dialogTitle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  dialogActions: {
    justifyContent: 'space-between',
    height: 57,
    padding: '0 24px'
  },
  inputs: {
    margin: '4px 12px',
    '& .MuiInputLabel-asterisk': {
      color: colors.rustRed
    }
  },
  attributeSelect: {
    [theme.breakpoints.down(breakpoints.sm)]: {
      flex: 1
    },
    [theme.breakpoints.up(breakpoints.sm)]: {
      flex: '0 0 200px'
    },
    [theme.breakpoints.up(breakpoints.md)]: {
      flex: '0 0 300px'
    }
  },
  filterOperatorSelect: {
    flex: '0 0 200px',
    [theme.breakpoints.up(breakpoints.xs)]: {
      flex: '0 0 225px'
    }
  },
  filterMenu: {
    flex: '1 0 480px',
    '& .MuiSelect-root': {
      paddingTop: 6,
      paddingBottom: 6,
      minHeight: 28
    }
  },
  delete: {
    flex: '0 0 48px',
    marginRight: 12,
    zIndex: 1,
    '&.Mui-disabled': {
      opacity: 0.5
    }
  },
  addControls: {
    display: 'flex',
    'align-items': 'center',
    'justify-content': 'flex-end'
  },
  attributePadding: {
    paddingTop: 10,
    paddingBottom: 10
  },
  filterPadding: {
    paddingRight: 10
  },
  groupByContainter: {
    display: 'flex',
    flexWrap: 'wrap'
  },
  operatorText: {
    fontWeight: 600,
    textTransform: 'uppercase'
  }
}));

type SelectedRange = {
  min: number;
  max: number;
};

type DateRangeOptions = {
  rangeType: string;
  nDaysValue?: string;
  numberOfDays?: number;
  min?: moment.Moment;
  max?: moment.Moment;
};

type TransactionalFilterSelect = {
  filterId: number;
  columnName: string;
  name: string;
  filterOperator: SegmentFilterOperator;
  filterType: string;
  transactionalFilterOptions: TransactionalFilter;
  dateRange?: DateRangeOptions;
  range?: SelectedRange;
  options?: string[];
  searchText?: string;
};

const emptyFilter: TransactionalFilterSelect = {
  filterId: undefined,
  columnName: undefined,
  name: undefined,
  filterOperator: SegmentFilterOperator.Is,
  filterType: undefined,
  transactionalFilterOptions: undefined
};

enum GroupByAggregateFunction {
  'Sum of' = 0,
  'Average' = 1,
  'Count of' = 2,
  'Distinct count of' = 3,
  'Minimum' = 4,
  'Maximum' = 5
}

enum GroupByOperatorChipLabel {
  '>' = 0,
  '<' = 1,
  '=' = 2
}

/**
 * Select menu and accompanying modal dialog for filtering a single attribute of filterType: transactional when building a segment
 */
const AttributeFilterTransactional = ({ attribute, selectedAttribute, position, exclude, attributeIndex }: Props) => {
  const overrides = useStyles();
  const { dispatch } = useBuildSegment(exclude);
  const [dialogOpen, setDialogOpen] = React.useState<boolean>(false);

  /** Currently checked filters in the filter dialog, not yet applied to select menu */
  const originalSelectedAttribute = React.useRef(selectedAttribute);
  const [selectedFilters, setSelectedFilters] = React.useState<TransactionalFilterSelect[]>([]);
  const [transactionalFilterOperator, setTransactionalFilterOperator] = React.useState<SegmentAttributeOperator>(
    selectedAttribute.transactionalFilters?.operator ?? SegmentAttributeOperator.And
  );

  const [groupBy, setGroupBy] = React.useState<GroupByFilter>(
    selectedAttribute.groupBy ? selectedAttribute.groupBy : null
  );

  const [disableApply, setDisableApply] = React.useState<boolean>(false);

  React.useEffect(() => {
    setDisableApply(
      groupBy?.groupByFilterId &&
        !!groupBy.groupByValues.groupByPredicates.find((predicate) => !predicate.valueFilterId || !predicate.value)
    );
  }, [groupBy]);

  React.useEffect(() => {
    if (selectedAttribute.transactionalFilters) {
      buildOutFilters();
    }
  }, []);

  React.useEffect(() => {
    originalSelectedAttribute.current = selectedAttribute;
  }, [selectedAttribute]);

  const buildOutFilters = () => {
    const createdFilters = [];
    originalSelectedAttribute.current.transactionalFilters.predicates.forEach((filter: any) => {
      const newTransactionalFilter = createBaseTransactionalFilter(filter.filterId);
      const baseFilter = {
        filterId: filter.filterId,
        columnName: filter.columnName,
        name: filter.name,
        filterOperator: filter.filterOperator,
        filterType: filter.filterType,
        transactionalFilterOptions: newTransactionalFilter
      };

      switch (baseFilter.transactionalFilterOptions?.filterType.toLowerCase()) {
        case 'datetime':
          if ('dateRange' in filter) {
            createdFilters.push({
              ...baseFilter,
              dateRange: {
                rangeType: 'custom',
                min: filter.dateRange.min ? moment(filter.dateRange.min) : null,
                max: filter.dateRange.max ? moment(filter.dateRange.max) : null
              }
            });
          }
          if ('numberOfDays' in filter) {
            createdFilters.push({
              ...baseFilter,
              dateRange: {
                rangeType: 'n-days',
                numberOfDays: filter.numberOfDays
              }
            });
          }
          break;
        case 'range':
          createdFilters.push({
            ...baseFilter,
            range: filter.range
          });
          break;
        case 'options':
          if ('searchText' in filter) {
            createdFilters.push({
              ...baseFilter,
              filterType: 'options',
              searchText: filter.searchText
            });
          } else {
            createdFilters.push({
              ...baseFilter,
              options: filter.options
            });
          }
          break;
        default:
          createdFilters.push({ ...baseFilter });
      }
    });
    setSelectedFilters(createdFilters);
  };

  const handleApplyFilterOptions = (filter: any, index: number = 0) => {
    const clonedFilter = _.cloneDeep(selectedFilters);
    clonedFilter[index] = { ...filter };
    setSelectedFilters(clonedFilter);
  };

  React.useEffect(() => {
    if (!!dialogOpen && selectedFilters.length < 1) {
      setSelectedFilters([emptyFilter]);
    }
  }, [dialogOpen]);

  const handleFilterOperator = (newSelectedOperator: SegmentFilterOperator, index: number = 0) => {
    const clonedFilter = _.cloneDeep(selectedFilters);
    clonedFilter[index].filterOperator = newSelectedOperator;

    // Check if switching between search text and options
    if (
      'options' in clonedFilter[index] &&
      newSelectedOperator !== SegmentFilterOperator.Is &&
      newSelectedOperator !== SegmentFilterOperator['Is Not']
    ) {
      clonedFilter[index] = _.omit(clonedFilter[index], ['options']);
      clonedFilter[index].searchText = '';
    }
    if (
      'searchText' in clonedFilter[index] &&
      (newSelectedOperator === SegmentFilterOperator.Is || newSelectedOperator === SegmentFilterOperator['Is Not'])
    ) {
      clonedFilter[index] = _.omit(clonedFilter[index], ['searchText']);
      clonedFilter[index].options = [];
    }
    setSelectedFilters([...clonedFilter]);
  };

  const changedTransactionalFilter = (baseTransactional: any, index: number = 0) => {
    if (selectedFilters.length === 0) {
      setSelectedFilters([{ ...baseTransactional }]);
    } else {
      const clonedFilter = _.cloneDeep(selectedFilters);
      clonedFilter[index] = { ...baseTransactional };
      setSelectedFilters(clonedFilter);
    }
  };

  const createBaseTransactionalFilter = (selectedId: number): TransactionalFilter => {
    //@ts-ignore
    const newTransactionalFilter = Object.assign(
      {},
      attribute.transactionalFilters.find((f) => f.filterId === selectedId)
    );
    if (!newTransactionalFilter) {
      return undefined;
    }

    if (typeof newTransactionalFilter.filters === 'string') {
      newTransactionalFilter.filters = newTransactionalFilter.filters ? JSON.parse(newTransactionalFilter.filters) : [];
    }
    if (newTransactionalFilter?.filterType.toLowerCase() === 'datetime') {
      newTransactionalFilter.filters = {
        ...newTransactionalFilter.filters,
        min: moment(newTransactionalFilter.filters.min),
        max: moment(newTransactionalFilter.filters.max)
      };
    }

    return newTransactionalFilter;
  };

  const handleChangeTransactionalFilter = (selectedId: number, filterIndex: number) => {
    const newTransactionalFilter = createBaseTransactionalFilter(selectedId);
    const baseFilter = {
      filterId: newTransactionalFilter.filterId,
      columnName: newTransactionalFilter.columnName,
      name: newTransactionalFilter.name,
      filterOperator: SegmentFilterOperator.Is,
      filterType: newTransactionalFilter.filterType,
      transactionalFilterOptions: newTransactionalFilter
    };

    if (baseFilter?.filterType.toLowerCase() === 'datetime') {
      changedTransactionalFilter(
        {
          ...baseFilter,
          dateRange: { min: null, max: null }
        },
        filterIndex
      );
    }
    if (baseFilter?.filterType.toLowerCase() === 'range') {
      changedTransactionalFilter(
        {
          ...baseFilter,
          range: {
            min: baseFilter.transactionalFilterOptions.filters.min,
            max: baseFilter.transactionalFilterOptions.filters.max
          }
        },
        filterIndex
      );
    }
    if (baseFilter.transactionalFilterOptions?.filterType.toLowerCase() === 'options') {
      changedTransactionalFilter(
        {
          ...baseFilter,
          options: []
        },
        filterIndex
      );
    }
  };

  /** Reset to previous filters on cancel */
  const handleCancel = () => {
    if (originalSelectedAttribute.current.transactionalFilters) {
      buildOutFilters();
      setTransactionalFilterOperator(originalSelectedAttribute.current.transactionalFilters.operator);
    } else {
      setSelectedFilters([]);
      setTransactionalFilterOperator(0);
    }
    if (originalSelectedAttribute.current.groupBy) {
      setGroupBy(originalSelectedAttribute.current.groupBy);
    } else {
      setGroupBy(null);
    }
    setDialogOpen(false);
  };

  const handleApply = () => {
    const completedFilters = selectedFilters.filter((f) => !!f.filterId);
    updateSelectedAttribute(completedFilters, groupBy);
    setDialogOpen(false);
  };

  const buildOptionChipLabel = (value: any) => {
    if ('searchText' in value) {
      return <React.Fragment>{value.searchText}</React.Fragment>;
    }
    let optionsList = '';
    value.options.forEach((option, index) => {
      if (index === value.options.length - 1) {
        optionsList = optionsList.concat(`${option}`);
      } else {
        optionsList = optionsList.concat(`${option}, `);
      }
    });
    return <React.Fragment>{optionsList}</React.Fragment>;
  };

  const buildRangeChipLabel = (value: any) => {
    return (
      <React.Fragment>
        <span className={overrides.filterPadding}>
          {!!value.range.min &&
            value.range.min !== value.transactionalFilterOptions.filters.min &&
            `Min: ${value.range.min}`}
        </span>
        <span>
          {!!value.range.max &&
            value.range.max !== value.transactionalFilterOptions.filters.max &&
            `Max: ${value.range.max}`}
        </span>
      </React.Fragment>
    );
  };

  const buildDateTimeChipLabel = (value: any) => {
    if (value.dateRange.rangeType === 'n-days') {
      return <React.Fragment>Last {value.dateRange.numberOfDays} days</React.Fragment>;
    } else {
      return (
        <React.Fragment>
          <span className={overrides.filterPadding}>
            {value.dateRange.min && `Start: ${value.dateRange.min.format('MM/DD/YYYY')}`}
          </span>
          <span>{value.dateRange.max && `End: ${value.dateRange.max.format('MM/DD/YYYY')}`}</span>
        </React.Fragment>
      );
    }
  };

  const buildGroupByLabel = (selectedGroupByFilter: GroupByFilter) => {
    const selectedOperator = selectedGroupByFilter.groupByValues.groupByOperator;
    const selectedGroupByValues = (
      <React.Fragment>
        {selectedGroupByFilter.groupByValues.groupByPredicates.map((predicate, index) => {
          return (
            <div key={`${predicate.valueFilterId} - ${index}`}>
              {index > 0 && <span>&nbsp;</span>}
              <span className={overrides.operatorText}>
                {GroupByAggregateFunction[predicate.aggregateFunction]}
              </span>{' '}
              {predicate.valueFilterName}{' '}
              <span className={overrides.operatorText}>{GroupByOperatorChipLabel[predicate.aggregateOperator]}</span>{' '}
              {predicate.value}
              {index + 1 < selectedGroupByFilter.groupByValues.groupByPredicates.length ? (
                <span className={overrides.operatorText}> {SegmentAttributeOperator[selectedOperator]}</span>
              ) : (
                ''
              )}
            </div>
          );
        })}
      </React.Fragment>
    );
    return (
      <div className={overrides.groupByContainter}>
        Group By: {selectedGroupByFilter.groupByFilterName}
        {' ('}
        {selectedGroupByValues}
        {')'}
      </div>
    );
  };

  const handleChipDelete = (filterId: number) => {
    const filterIndex = selectedFilters.findIndex((f) => f.filterId === filterId);
    const clonedFilters = _.cloneDeep(selectedFilters);
    clonedFilters.splice(filterIndex, 1);
    updateSelectedAttribute(clonedFilters, groupBy);
  };

  const updateSelectedAttribute = (currentFilters: TransactionalFilterSelect[], currentGroupBy: GroupByFilter) => {
    const updatedGroupBy = _.cloneDeep(currentGroupBy);
    let filterArray = currentFilters.map((filter: TransactionalFilterSelect) => {
      if (filter.filterType === 'datetime') {
        if (filter.dateRange?.rangeType === 'n-days') {
          const clonedFilter = _.omit(filter, ['dateRange']);
          return {
            ...clonedFilter,
            numberOfDays: Number(filter.dateRange.numberOfDays)
          };
        }
        return {
          ...filter,
          dateRange: {
            max: filter.dateRange?.max?.toISOString() ?? null,
            min: filter.dateRange?.min?.toISOString() ?? null
          }
        };
      }

      if ('searchText' in filter) {
        return _.omit(filter, ['filterType']);
      }
      return { ...filter };
    });
    if (filterArray.length > 0) {
      dispatch({
        type: 'updateTransactionalFilter',
        attribute: {
          ...selectedAttribute,
          transactionalFilters: {
            operator: transactionalFilterOperator,
            predicates: filterArray as unknown as SelectedAttribute[]
          },
          groupBy: updatedGroupBy
        },
        position,
        attributeIndex: attributeIndex
      });
    } else {
      const { transactionalFilters: _, ...rest } = selectedAttribute;
      dispatch({
        type: 'updateTransactionalFilter',
        attribute: {
          ...rest,
          groupBy: updatedGroupBy
        },
        position,
        attributeIndex: attributeIndex
      });
    }
    setSelectedFilters(currentFilters);
    setGroupBy(updatedGroupBy);
  };

  const handleAddAnotherAttribute = () => {
    const clonedFilters = _.cloneDeep(selectedFilters);
    clonedFilters.push(emptyFilter);
    setSelectedFilters(clonedFilters);
  };

  const handleDeleteFilterRow = (filterIndex: number) => {
    const clonedFilters = _.cloneDeep(selectedFilters);
    clonedFilters.splice(filterIndex, 1);
    if (clonedFilters.length === 0) {
      clonedFilters.push(emptyFilter);
    }
    setSelectedFilters(clonedFilters);
  };

  /** Function passed to filter select `renderValue` prop: conditionally render the custom content of the filter multi-select menu */
  const renderFilterValues = (selectedTransactionalFilters: any[]) => {
    if (dialogOpen) {
      return <span></span>;
    }

    if (!attribute.transactionalFilters || attribute.transactionalFilters.length < 1) {
      return <span className={buildStyles.filterText}>There are no filters to apply</span>;
    }

    if (selectedTransactionalFilters.filter((f) => !!f.filterId).length === 0) {
      return (
        <div className={buildStyles.transactionalFilterChipsContainer}>
          <span className={buildStyles.filterText}>All - click for additional filtering</span>
          {!!groupBy?.groupByFilterId && (
            <MultiselectChip
              className={buildStyles.groupBySelections}
              label={buildGroupByLabel(groupBy)}
              size="small"
              deleteIcon={<Clear onMouseDown={(event) => event.stopPropagation()} aria-label="delete filter" />}
              onDelete={() => {
                const clonedFilters = _.cloneDeep(selectedFilters);
                updateSelectedAttribute(clonedFilters, null);
              }}
            />
          )}
        </div>
      );
    }

    return (
      <div className={buildStyles.filterChips}>
        {selectedTransactionalFilters.map((value, index) => {
          const lastChip = index === selectedTransactionalFilters.length - 1;

          const filterName =
            value.name.trim() === '' ? (
              <span className={buildStyles.blankOption}>blank</span>
            ) : (
              <span className={buildStyles.transactionalOptionName}>{value.name}</span>
            );

          let chipLabel;
          switch (value.filterType.toLowerCase()) {
            case 'options':
              chipLabel = (
                <React.Fragment>
                  {filterName}: {buildOptionChipLabel(value)}
                </React.Fragment>
              );
              break;
            case 'range':
              chipLabel = (
                <React.Fragment>
                  {filterName}: {buildRangeChipLabel(value)}
                </React.Fragment>
              );
              break;
            case 'datetime':
              chipLabel = (
                <React.Fragment>
                  {filterName}: {buildDateTimeChipLabel(value)}
                </React.Fragment>
              );
              break;
            default:
              chipLabel = <React.Fragment>{filterName}</React.Fragment>;
          }

          return (
            <div
              key={`Multi-Chip-${value.filterId}-${index}`}
              className={buildStyles.transactionalFilterChipsContainer}
            >
              <MultiselectChip
                className={buildStyles.transactionalFilters}
                label={chipLabel}
                size="small"
                deleteIcon={<Clear onMouseDown={(event) => event.stopPropagation()} aria-label="delete filter" />}
                onDelete={() => handleChipDelete(value.filterId)}
              />
              {!lastChip && (
                <span className={buildStyles.transactionalGroupOperatorText}>
                  {SegmentAttributeOperator[transactionalFilterOperator]}
                </span>
              )}
              {!!groupBy?.groupByFilterId && (
                <MultiselectChip
                  className={buildStyles.groupBySelections}
                  label={buildGroupByLabel(groupBy)}
                  size="small"
                  deleteIcon={<Clear onMouseDown={(event) => event.stopPropagation()} aria-label="delete filter" />}
                  onDelete={() => {
                    const clonedFilters = _.cloneDeep(selectedFilters);
                    updateSelectedAttribute(clonedFilters, null);
                  }}
                />
              )}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <React.Fragment>
      <Select
        id={`filter-attribute-${attribute.id}`}
        multiple={true}
        disabled={!attribute.transactionalFilters || attribute.transactionalFilters.length < 1}
        value={selectedFilters}
        open={false}
        onOpen={() => {
          setDialogOpen(true);
        }}
        renderValue={renderFilterValues}
        displayEmpty={true}
      />
      <Dialog
        open={dialogOpen}
        scroll="paper"
        className={overrides.dialog}
        aria-labelledby="select-attribute-dialog-title"
      >
        <DialogTitle className={overrides.dialogTitle} id="select-attribute-dialog-title" disableTypography>
          <h3 className={buildStyles.dialogTitle}>Select Filters</h3>
        </DialogTitle>
        <DialogContent dividers>
          <div className={buildStyles.buildSegmentRoot}>
            {selectedFilters.map((parameterSet, index) => (
              <div
                key={`Selected-Filter-${parameterSet.filterId}-${index}`}
                className={clsx(styles.attribute, overrides.attributePadding)}
              >
                {index === 0 && <div className={styles.attributeOperatorText}></div>}
                {index === 1 && (
                  <OperatorSelect
                    operator={transactionalFilterOperator}
                    handleChange={(operator) => setTransactionalFilterOperator(operator)}
                  />
                )}
                {index > 1 && (
                  <div className={styles.attributeOperatorText}>
                    <p className={styles.groupOperatorText}>{SegmentAttributeOperator[transactionalFilterOperator]}</p>
                  </div>
                )}
                <FilterOptions
                  transactionalAttribute={attribute as TransactionalAttribute}
                  handleChangeTransactionalFilter={handleChangeTransactionalFilter}
                  selectedAttribute={selectedAttribute}
                  selectedFilter={parameterSet}
                  handleApplyFilterOptions={handleApplyFilterOptions}
                  filterIndex={index}
                  handleFilterOperator={handleFilterOperator}
                  handleDeleteFilterRow={handleDeleteFilterRow}
                />
              </div>
            ))}
          </div>
          <div className={overrides.addControls}>
            <AddAttribute label="Add another Filter" handleClick={handleAddAnotherAttribute} />
          </div>
          <AttributeGroupBy
            transactionalFilterOptions={attribute.transactionalFilters}
            selectedGroupBy={groupBy}
            updateGroupBy={(newGroupBy: GroupByFilter) => {
              setGroupBy(newGroupBy);
            }}
          />
        </DialogContent>
        <DialogActions className={overrides.dialogActions}>
          <TertiaryButton text="Cancel" title="Cancel" component="button" handleClick={handleCancel} />
          <SecondaryButton text="Apply" title="Apply" handleClick={handleApply} deactive={disableApply} />
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
};

type FilterOptionsProps = {
  transactionalAttribute: TransactionalAttribute;
  handleChangeTransactionalFilter: (filterId: number, filterIndex: number) => void;
  selectedAttribute: SelectedAttributeTransactional;
  selectedFilter?: any;
  handleApplyFilterOptions: (attributes: any, index?: number) => void;
  filterIndex?: number;
  handleFilterOperator: (selectedOperator: number, index?: number) => void;
  handleDeleteFilterRow: (index: number) => void;
};

function buildDate(dateFilter: DateRangeOptions) {
  if (dateFilter.rangeType === 'n-days') {
    return { numberOfDays: dateFilter.numberOfDays };
  } else {
    return {
      dateRange: {
        min: dateFilter.min ? moment(dateFilter.min) : null,
        max: dateFilter.max ? moment(dateFilter.max) : null
      }
    };
  }
}

const FilterOptions = ({
  transactionalAttribute,
  handleChangeTransactionalFilter,
  selectedAttribute,
  selectedFilter,
  handleApplyFilterOptions,
  filterIndex,
  handleFilterOperator,
  handleDeleteFilterRow
}: FilterOptionsProps) => {
  const overrides = useStyles();
  const [filterOptions, setFilterOptions] = React.useState<string[]>(
    selectedFilter.options ? selectedFilter.options : []
  );
  const [filterText, setFilterText] = React.useState<string>(
    selectedFilter.searchText ? selectedFilter.searchText : ''
  );
  const [dateRangeFilters, setDateRangeFilters] = React.useState<any>(
    selectedFilter.dateRange ? buildDate(selectedFilter.dateRange) : null
  );
  const [rangeFilters, setRangeFilters] = React.useState<SelectedRange>({
    min: selectedFilter.range?.min ?? null,
    max: selectedFilter.range?.max ?? null
  });

  React.useEffect(() => {
    if (selectedFilter?.filterId && selectedFilter?.filterType) {
      switch (selectedFilter.filterType) {
        case 'options':
          setFilterText(selectedFilter.searchText);
          setFilterOptions(selectedFilter.options);
          break;
        case 'datetime':
          if (selectedFilter.dateRange && selectedFilter.dateRange.rangeType === 'n-days') {
            setDateRangeFilters({
              numberOfDays: selectedFilter.dateRange.numberOfDays
            });
          } else {
            setDateRangeFilters({
              dateRange: {
                min: selectedFilter.dateRange?.min ? moment(selectedFilter.dateRange?.min) : null,
                max: selectedFilter.dateRange?.max ? moment(selectedFilter.dateRange?.max) : null
              }
            });
          }
          break;
        case 'range':
          setRangeFilters({
            min: selectedFilter.range?.min ?? null,
            max: selectedFilter.range?.max ?? null
          });
          break;
      }
    }
  }, [selectedFilter]);

  const updateSelectedOptions = (options: string[]) => {
    if (selectedFilter) {
      const updatedFilter = {
        ...selectedFilter,
        options: options
      };
      handleApplyFilterOptions(updatedFilter, filterIndex);
      setFilterOptions(options);
    }
  };

  const updateSelectedSearchText = (searchString: string) => {
    if (searchString) {
      const updatedFilter = {
        ...selectedFilter,
        searchText: searchString
      };
      handleApplyFilterOptions(updatedFilter, filterIndex);
      setFilterText(searchString);
    }
  };

  const updateSelectedDateRange = (options: DateRangeOptions) => {
    if (selectedFilter) {
      const updatedFilter = {
        ...selectedFilter,
        dateRange: options
      };
      handleApplyFilterOptions(updatedFilter, filterIndex);
      if (options.rangeType === 'n-days') {
        setDateRangeFilters({
          numberOfDays: Number(options.nDaysValue)
        });
      } else {
        setDateRangeFilters({
          dateRange: {
            min: options.min ?? null,
            max: options.max ?? null
          }
        });
      }
    }
  };

  const updateSelectedRange = (options: SelectedRange) => {
    if (selectedFilter) {
      const updatedFilter = {
        ...selectedFilter,
        range: options
      };
      handleApplyFilterOptions(updatedFilter, filterIndex);
      setRangeFilters(options);
    }
  };

  return (
    <div>
      <div className={styles.controls}>
        <div className={styles.inputGroup}>
          <FormControl
            size="small"
            variant="outlined"
            className={clsx(overrides.inputs, overrides.attributeSelect)}
            required={true}
          >
            <InputLabel id="transactional-filter-selection">Transactional Filters</InputLabel>
            <Select
              labelId="transactional-filter-selection"
              value={selectedFilter?.filterId ?? ''}
              onChange={(event: any) => handleChangeTransactionalFilter(Number(event.target.value), filterIndex)}
            >
              {transactionalAttribute.transactionalFilters?.map((filter) => (
                <MenuItem value={filter.filterId} key={`Filter-Option-${filter.filterId}`}>
                  {filter.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl
            size="small"
            variant="outlined"
            className={clsx(overrides.inputs, overrides.filterOperatorSelect)}
            required={true}
          >
            <InputLabel id="filter-logic">Filter Logic</InputLabel>
            <Select
              labelId="transactional-filter-selection"
              value={selectedFilter?.filterOperator ?? SegmentFilterOperator.Is}
              disabled={!selectedFilter.transactionalFilterOptions}
              onChange={(event: any) => {
                handleFilterOperator(event.target.value, filterIndex);
              }}
            >
              <MenuItem key="Is" value={SegmentFilterOperator.Is}>
                Is
              </MenuItem>
              ,
              <MenuItem key="Is Not" value={SegmentFilterOperator['Is Not']}>
                Is Not
              </MenuItem>
              {selectedFilter &&
                !(
                  'range' in selectedFilter ||
                  'dateRange' in selectedFilter ||
                  'numberOfDays' in selectedFilter ||
                  'where' in selectedFilter
                ) && [
                  <MenuItem key="Begins With" value={SegmentFilterOperator['Begins With']}>
                    Begins With
                  </MenuItem>,
                  <MenuItem key="Does Not Begin With" value={SegmentFilterOperator['Does Not Begin With']}>
                    Does Not Begin With
                  </MenuItem>,
                  <MenuItem key="Ends With" value={SegmentFilterOperator['Ends With']}>
                    Ends With
                  </MenuItem>,
                  <MenuItem key="Does Not End With" value={SegmentFilterOperator['Does Not End With']}>
                    Does Not End With
                  </MenuItem>,
                  <MenuItem key="Contains" value={SegmentFilterOperator['Contains']}>
                    Contains
                  </MenuItem>,
                  <MenuItem key="Does Not Contain" value={SegmentFilterOperator['Does Not Contain']}>
                    Does Not Contain
                  </MenuItem>
                ]}
            </Select>
          </FormControl>
          <FormControl size="small" variant="outlined" className={clsx(overrides.inputs, overrides.filterMenu)}>
            {!selectedFilter.filterId && (
              <>
                <InputLabel htmlFor={`filter-attribute-0`}>Filter the Attribute</InputLabel>
                <Select id="filter-attribute-0" value="" disabled />
              </>
            )}
            {!evalAttributeFilterRenderType(selectedFilter, selectedFilter) && (
              <InputLabel htmlFor={`filter-attribute-${selectedFilter.filterId}`} shrink={true}>
                Select a Filter
              </InputLabel>
            )}
            {evalAttributeFilterRenderType(selectedFilter, selectedFilter) === SegmentAttributeFilterRender.Input && (
              <AttributeFilterInput
                attribute={selectedFilter.transactionalFilterOptions as unknown as SegmentAttributeOptions}
                selectedAttribute={{ ...selectedFilter, searchText: filterText } as SelectedAttributeInput}
                position={[]}
                applyTransactional={(searchString: string) => {
                  updateSelectedSearchText(searchString);
                }}
              />
            )}
            {evalAttributeFilterRenderType(selectedFilter, selectedFilter) === SegmentAttributeFilterRender.Options && (
              <AttributeFilterOptions
                attribute={selectedFilter.transactionalFilterOptions as unknown as SegmentAttributeOptions}
                selectedAttribute={
                  { ...selectedAttribute, options: filterOptions ?? [] } as unknown as SelectedAttributeOption
                }
                position={[]}
                applyTransactional={(options: string[]) => {
                  updateSelectedOptions(options);
                }}
                deleteTransactionalChip={(option: string) => {
                  const updatedOptions = filterOptions.filter((op) => op !== option);
                  updateSelectedOptions(updatedOptions);
                }}
              />
            )}
            {evalAttributeFilterRenderType(selectedFilter, selectedFilter) ===
              SegmentAttributeFilterRender.Datetime && (
              <AttributeFilterDatetime
                //@ts-ignore
                attribute={selectedFilter.transactionalFilterOptions as unknown as SegmentAttributeDatetime}
                selectedAttribute={
                  { ...selectedAttribute, ...dateRangeFilters } as unknown as SelectedAttributeDaterange
                }
                applyTransactional={(options: DateRangeOptions) => {
                  updateSelectedDateRange(options);
                }}
                position={[]}
              />
            )}
            {evalAttributeFilterRenderType(selectedFilter, selectedFilter) === SegmentAttributeFilterRender.Range && (
              <AttributeFilterRange
                //@ts-ignore
                attribute={selectedFilter.transactionalFilterOptions as unknown as SegmentAttributeRange}
                selectedAttribute={
                  {
                    ...selectedAttribute,
                    range: {
                      ...rangeFilters
                    }
                  } as unknown as SelectedAttributeRange
                }
                applyTransactional={(options: any) => {
                  updateSelectedRange(options);
                }}
                position={[]}
              />
            )}
          </FormControl>
          <IconButton
            onClick={() => handleDeleteFilterRow(filterIndex)}
            className={overrides.delete}
            disabled={filterIndex === 0 && !selectedFilter.filterId}
          >
            <Delete htmlColor={blue[600]} />
          </IconButton>
        </div>
      </div>
    </div>
  );
};

export default AttributeFilterTransactional;
